import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import {
  suggestGarAddresses,
  type GarSuggestItem,
} from "@/services/gar-api.service";
import { cn } from "@/lib/utils";

type Props = {
  address: string;
  garUuid: string | null;
  onAddressChange: (v: string) => void;
  onGarUuidChange: (uuid: string | null, formatted: string) => void;
  className?: string;
  /** Подпись поля (по умолчанию «Адрес (ГАР)»; в карточке — label шаблона) */
  label?: string;
  /** Скрыть встроенный Label+справку, если родитель рисует свой заголовок */
  hideLabel?: boolean;
};

export function GarAddressField({
  address,
  garUuid,
  onAddressChange,
  onGarUuidChange,
  className,
  label = "Адрес (ГАР)",
  hideLabel = false,
}: Props) {
  const [q, setQ] = useState(address);
  const [items, setItems] = useState<GarSuggestItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setQ(address);
  }, [address]);

  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) {
      setItems([]);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      setLoading(true);
      try {
        const res = await suggestGarAddresses(t, 8);
        if (!cancelled) {
          setItems(res.items || []);
          setOpen(true);
        }
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [q]);

  return (
    <div className={cn("space-y-2 md:col-span-2 relative", className)}>
      {!hideLabel ? (
        <div className="flex items-center gap-1">
          <Label htmlFor="address">{label}</Label>
          <SectionHelpTooltip label="Справка: адрес ГАР">
            <p>
              Подсказки из локального Государственного адресного реестра (ГАР).
              Сохраняется GUID и нормализованная строка — не путать с поиском мест
              Nominatim на карте.
            </p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Введите ≥2 символа — выберите подсказку</li>
              <li>«Очистить ГАР» снимает GUID, текст можно править вручную</li>
              <li>Esc закрывает список подсказок</li>
            </ul>
          </SectionHelpTooltip>
        </div>
      ) : null}
      <Input
        id="address"
        placeholder="Начните вводить официальный адрес…"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          onAddressChange(e.target.value);
          if (garUuid) onGarUuidChange(null, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
        }}
        autoComplete="off"
      />
      {garUuid ? (
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <code className="truncate">ГАР: {garUuid}</code>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7"
            onClick={() => onGarUuidChange(null, address)}
          >
            Очистить ГАР
          </Button>
        </div>
      ) : null}
      {open && (items.length > 0 || loading) ? (
        <ul className="absolute z-50 mt-1 max-h-48 w-full overflow-auto rounded-md border bg-popover p-1 shadow-md">
          {loading && items.length === 0 ? (
            <li className="px-2 py-1.5 text-xs text-muted-foreground">Поиск…</li>
          ) : null}
          {items.map((it) => (
            <li key={it.gar_uuid}>
              <button
                type="button"
                className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
                onClick={() => {
                  onGarUuidChange(it.gar_uuid, it.formatted_address);
                  setQ(it.formatted_address);
                  setOpen(false);
                }}
              >
                {it.formatted_address}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
